import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { ReactNode, useEffect, useRef } from 'react';

import { AnalyticsEvent } from '@/hooks/useAnalytics';
import { useAuth } from '@/state/auth';

// StatsigWrapper provides feature flag context using user authentication
function StatsigWrapper({ children }: { children: ReactNode }) {
  const { whoami, me, ssoDomain } = useAuth();
  const previousMe = useRef(me); // previousMe is used to detect login transitions.

  const userId = whoami?.principal.name || 'anonymous';

  const statsigUser = {
    userID: userId,
    email: userId,
    custom: {
      isPraetorian: whoami?.principal.praetorian ?? false,
      isAssuming: whoami?.is_assuming ?? false,
      tenantName: whoami?.tenant?.name || '',
      tenantDisplayName: whoami?.tenant?.display_name || '',
      tenantCustomerType: whoami?.tenant?.customer_type || '',
      principalName: whoami?.principal.name || '',
      principalDisplayName: whoami?.principal.display_name || '',
      principalAccessType: whoami?.principal.access_type || '',
      principalSsoDomain: whoami?.principal.sso_domain || '',
      role: whoami?.role || '',
    },
  };

  const clientKey = import.meta.env.VITE_STATSIG_CLIENT_KEY || '';

  // Use async initialization hook to fetch latest feature flag values
  const { client } = useClientAsyncInit(clientKey, statsigUser, {
    plugins: [new StatsigAutoCapturePlugin()],
  });

  // Update Statsig user identity when auth state changes (e.g., after login or impersonation).
  // useClientAsyncInit only sets the user on first initialization;
  // this effect syncs the user when whoami changes.
  // Note: flushSync in startImpersonation/stopImpersonation ensures this effect
  // runs before navigate() triggers auto-capture events.
  useEffect(() => {
    // Detect logout: me transitioned from populated to empty
    if (client && previousMe.current && !me) {
      client.logEvent(AnalyticsEvent.AUTH_LOGOUT);
      previousMe.current = me;
      return;
    }

    if (!client || !me) return;

    client.updateUserSync(statsigUser);

    // Detect login: me transitioned from empty to populated
    if (!previousMe.current) {
      client.logEvent(AnalyticsEvent.AUTH_LOGIN, undefined, {
        method: ssoDomain ? 'sso' : 'password',
        sso_domain: ssoDomain || '',
      });
    }

    previousMe.current = me;
  }, [client, whoami]);

  // Always wrap in StatsigProvider so child components can safely use hooks like useGateValue
  return <StatsigProvider client={client}>{children}</StatsigProvider>;
}

export default StatsigWrapper;
